import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Button,
  CardActions,
  CardContent,
  Dialog,
  Typography,
} from "@mui/material";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import ReactGA from "react-ga4";
import { getTokenInfo, useAuth0 } from "contexts/AuthManager/Auth";
import { MIDGARD_CUSTOM_URL_LS_KEY } from "App/CustomAskrUrlIndicator";

export type SightPageType = "analytics" | "throughput" | "logbook";

const SIGHT_PAGE_NAMES: Record<SightPageType, string> = {
  analytics: "Analytics",
  throughput: "Throughput",
  logbook: "Logbook",
};

export function RequestAnalyticsTrialDialog({
  open,
  handleClose,
}: {
  open: null | SightPageType;
  handleClose: () => void;
}) {
  const [requestSent, setRequestSent] = useState(false);
  // change button back to active state after close transition
  useEffect(() => {
    setTimeout(() => {
      setRequestSent(false);
    }, 300);
  }, [open]);
  const { translate } = useFlightTranslation();
  return (
    <Dialog
      open={Boolean(open)}
      onClose={() => {
        handleClose();
      }}
      hideBackdrop={true}
    >
      <CardContent style={{ paddingBottom: 12, maxWidth: 312 }}>
        <Typography gutterBottom sx={{ color: "text.secondary", fontSize: 14 }}>
          {translate(`Raven Sight — ${open && SIGHT_PAGE_NAMES[open]}`)}
        </Typography>
        <div style={requestSent ? { opacity: 0.5 } : {}}>
          <Typography>
            {translate("This feature has not been enabled.")}
          </Typography>
          <br />
          <Typography gutterBottom>
            {translate("Request a 7-day free trial of Raven Sight Analytics:")}
          </Typography>
        </div>

        <CardActions style={{ display: "flex", justifyContent: "end" }}>
          {requestSent ? (
            <Typography>
              {translate("Request sent!")}
              <br />
              {translate("We'll get back to you shortly.")}
            </Typography>
          ) : (
            <RequestTrialButton
              {...{ setRequestSent, open, handleClose, requestSent }}
            />
          )}
        </CardActions>
      </CardContent>
    </Dialog>
  );
}

function RequestTrialButton({
  setRequestSent,
  open,
  handleClose,
  requestSent,
}: {
  setRequestSent: Dispatch<SetStateAction<boolean>>;
  open: null | SightPageType;
  handleClose: () => void;
  requestSent;
}) {
  const { user } = useAuth0();
  const ckey = useCustomerKey();
  const { translate } = useFlightTranslation();
  const requestedFeatureName = open && SIGHT_PAGE_NAMES[open];
  return (
    <Button
      style={{ color: "white", textTransform: "none" }}
      variant="contained"
      onClick={async () => {
        // send a slack notification to channel #sight-requests
        const message = `user ${
          user.email ?? "no_user"
        } requested trial of Sight - ${requestedFeatureName}`;
        try {
          const resp = await sendEmail({
            ckey,
            to: ["support@raven.ai"],
            subject: `Sight Request - ${requestedFeatureName} - ${user.email}`,
            message,
            onSuccess: data => {
              console.log("email sent successfully");
              console.log(data);
              setRequestSent(true);
              setTimeout(() => {
                handleClose();
              }, 5 * 1000);
            },
            onError: msg => {
              console.error("Failed to send email:", msg);
            },
          });
          console.log(resp);
        } catch (e) {
          console.error(e);
        }

        ReactGA.event({
          category: "User",
          action: `Clicked Request Sight ${requestedFeatureName}`,
        });
      }}
      disabled={requestSent}
    >
      {translate("Request Trial")}
    </Button>
  );
}

const sendEmail = async ({
  subject,
  message,
  to,
  onSuccess,
  onError,
  ckey,
}: {
  subject: string;
  message: string;
  to: string[];
  onSuccess?: (data: any) => void;
  onError?: (msg: string) => void;
  ckey: string;
}) => {
  const cluster = "gke-us-east1";
  const service = "midgard";
  const prodUri = `https://${ckey}.${cluster}.raven-cloud.com/${service}/graphql/`;
  const customMidgardUrl = window.localStorage.getItem(
    MIDGARD_CUSTOM_URL_LS_KEY
  );
  let emailSendUrl = (customMidgardUrl ?? prodUri).replace(
    "graphql",
    "send_email"
  );
  if (!emailSendUrl.endsWith("/")) {
    emailSendUrl += "/";
  }
  console.log("sending email via", emailSendUrl);
  try {
    const response = await fetch(emailSendUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getTokenInfo().idToken}`,
      },
      body: JSON.stringify({
        subject: subject,
        message: message,
        to: to,
      }),
    });
    const data = await response.json();
    if (data.status === "success") {
      console.log("Email sent successfully");
      onSuccess?.(data);
    } else {
      console.error("Failed to send email:", data.message);
      onError?.(data.message);
    }
  } catch (error) {
    console.error("Error:", error);
  }
};
